/**
 * Paddle.js 공용 유틸리티
 *
 * 스크립트 로딩 + 초기화를 한 곳에서 관리하여
 * PricingPage(인라인 오버레이)와 PaddleCheckoutPage(URL 기반 폴백) 모두에서 사용합니다.
 *
 * 콜백은 `openPaddleCheckout` 호출 시마다 갱신되므로
 * 서로 다른 컴포넌트에서 순차적으로 사용해도 안전합니다.
 */

type PaddleEnvironment = 'sandbox' | 'production';

interface PaddleEvent {
  name?: string;
  data?: Record<string, unknown>;
}

export interface PaddleApi {
  Initialize: (options: {
    token: string;
    checkout?: { settings?: Record<string, unknown> };
    eventCallback?: (event: PaddleEvent) => void;
  }) => void;
  Checkout: {
    open: (options: { transactionId: string }) => void;
    close?: () => void;
  };
  Environment?: { set: (env: PaddleEnvironment) => void };
}

interface WindowWithPaddle extends Window {
  Paddle?: PaddleApi;
  __askyouraiPaddleInitialized?: boolean;
}

const PADDLE_JS_URL = 'https://cdn.paddle.com/paddle/v2/paddle.js';
const CLIENT_TOKEN = String(import.meta.env.VITE_PADDLE_CLIENT_TOKEN || '').trim();
const RAW_ENV = String(import.meta.env.VITE_PADDLE_ENV || 'sandbox').trim().toLowerCase();
const PADDLE_ENV: PaddleEnvironment =
  RAW_ENV === 'production' || RAW_ENV === 'prod' || RAW_ENV === 'live'
    ? 'production'
    : 'sandbox';

const OVERLAY_CLOSE_DELAY_MS = 250;

function _win(): WindowWithPaddle {
  return window as WindowWithPaddle;
}

let _onCompleted: (() => void) | null = null;
let _onClosed: (() => void) | null = null;

export function loadPaddleScript(): Promise<void> {
  if (_win().Paddle) return Promise.resolve();

  return new Promise((resolve, reject) => {
    let script = document.querySelector(
      'script[data-askyourai-paddle="1"]',
    ) as HTMLScriptElement | null;

    if (!script) {
      script = document.createElement('script');
      script.src = PADDLE_JS_URL;
      script.async = true;
      script.setAttribute('data-askyourai-paddle', '1');
      script.addEventListener('load', () => resolve(), { once: true });
      script.addEventListener('error', () => reject(new Error('Paddle 스크립트 로드 실패')), {
        once: true,
      });
      document.head.appendChild(script);
      return;
    }

    if (_win().Paddle) {
      resolve();
      return;
    }

    script.addEventListener('load', () => resolve(), { once: true });
    script.addEventListener('error', () => reject(new Error('Paddle 스크립트 로드 실패')), {
      once: true,
    });
  });
}

function ensureInitialized(): PaddleApi {
  const paddle = _win().Paddle;
  if (!paddle) throw new Error('Paddle SDK를 불러올 수 없습니다.');
  if (!CLIENT_TOKEN) throw new Error('VITE_PADDLE_CLIENT_TOKEN이 설정되지 않았습니다.');

  if (!_win().__askyouraiPaddleInitialized) {
    if (PADDLE_ENV === 'sandbox' && paddle.Environment?.set) {
      paddle.Environment.set('sandbox');
    }

    paddle.Initialize({
      token: CLIENT_TOKEN,
      checkout: { settings: { displayMode: 'overlay', locale: 'ko' } },
      eventCallback: (event: PaddleEvent) => {
        const eventName = String(event?.name || '').trim();

        if (eventName === 'checkout.closed') {
          _onClosed?.();
          return;
        }

        if (eventName === 'checkout.completed') {
          if (typeof paddle.Checkout.close === 'function') {
            window.setTimeout(() => {
              try {
                paddle.Checkout.close?.();
              } catch {
                /* overlay close failure — redirect below recovers UX */
              }
            }, OVERLAY_CLOSE_DELAY_MS);
          }
          _onCompleted?.();
        }
      },
    });
    _win().__askyouraiPaddleInitialized = true;
  }

  return paddle;
}

export interface PaddleCheckoutCallbacks {
  onCompleted?: () => void;
  onClosed?: () => void;
}

export async function openPaddleCheckout(
  transactionId: string,
  callbacks?: PaddleCheckoutCallbacks,
): Promise<void> {
  if (!transactionId) throw new Error('Transaction ID가 없습니다.');

  _onCompleted = callbacks?.onCompleted ?? null;
  _onClosed = callbacks?.onClosed ?? null;

  await loadPaddleScript();
  const paddle = ensureInitialized();
  paddle.Checkout.open({ transactionId });
}
